/* eslint-disable no-underscore-dangle */
import { usePaginationSearch } from '@application/utils';
import type { BreederReviewItemData } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useGetReviews } from './useGetReviews';

const REVIEWS_PAGINATED_SIZE = 3;

export const useReviewList = (breederId?: number) => {
  const { translate } = useTranslation();

  const { breederReviews, breederReviewsAggregate, breederReviewsRefetch, breederReviewsLoading } =
    useGetReviews(breederId);
  const { currentPage, handlePaginationChange } = usePaginationSearch({
    scrollToTopOnChange: false,
  });

  const reviewList = useMemo(() => {
    return {
      loading: breederReviewsLoading,
      amount: breederReviewsAggregate?._count?._all ?? undefined,
      average: breederReviewsAggregate?._avg?.globalGrade ?? undefined,
      categoryAverage: {
        cleaning: breederReviewsAggregate?._avg?.hygieneGrade ?? undefined,
        humanContact: breederReviewsAggregate?._avg?.humanContactGrade ?? undefined,
        followup: breederReviewsAggregate?._avg?.followUpGrade ?? undefined,
        wellBeing: breederReviewsAggregate?._avg?.dogsWellBeingGrade ?? undefined,
      },
      items: (breederReviews ?? []).map(
        (review) =>
          ({
            id: review.id,
            name: review.reviewerName ?? undefined,
            image: review.reviewerPic ?? undefined,
            description: review.description ?? undefined,
            average: review.globalGrade ?? undefined,
            date: dayjs(review?.createdAt)?.format?.('DD/MM/YYYY'),
            type: review.contactType
              ? translate(`enum.reviewContactType.${review.contactType}`)
              : undefined,
            verified: review.verified,
            answer: review.answer
              ? {
                  content: review.answer.content,
                  date: dayjs(review.answer.createdAt).format('DD/MM/YYYY'),
                  breederName: translate('common.sections.reviews.answer'),
                  breederPic: review.dogBreeder.profileImg ?? undefined,
                }
              : undefined,
          } as BreederReviewItemData),
      ),
      pagination: {
        forcePage: currentPage,
        onChange: async (changedPage: number) => {
          handlePaginationChange(changedPage);
          const wrapper = document.getElementsByClassName('review-list-wrapper').item(0);
          const scrollPosition = wrapper?.getBoundingClientRect().top;
          if (scrollPosition) {
            const top = scrollPosition + window.scrollY - 150;
            window.scrollTo({ left: 0, top });
          }
          await breederReviewsRefetch({
            skip: REVIEWS_PAGINATED_SIZE * changedPage,
          });
        },
        pageCount: (breederReviewsAggregate?._count?._all ?? 0) / REVIEWS_PAGINATED_SIZE,
      },
    };
  }, [
    breederReviews,
    breederReviewsAggregate?._avg?.dogsWellBeingGrade,
    breederReviewsAggregate?._avg?.followUpGrade,
    breederReviewsAggregate?._avg?.globalGrade,
    breederReviewsAggregate?._avg?.humanContactGrade,
    breederReviewsAggregate?._avg?.hygieneGrade,
    breederReviewsAggregate?._count?._all,
    breederReviewsLoading,
    breederReviewsRefetch,
    currentPage,
    handlePaginationChange,
    translate,
  ]);

  return { reviewList };
};
